'use client';

import { useLayoutEffect, useState, ReactNode } from 'react';
import { getAuthHint } from './auth-redirect';
import { getLastOrganization } from '@/lib/preferences';

interface AuthAwareContentProps {
  children: ReactNode;
}

/**
 * Wrapper component that handles the auth-aware content display.
 *
 * For SEO: Content is always server-rendered (crawlers see everything)
 * For logged-out users: Content shows immediately after JS check
 * For logged-in users: Content stays hidden while redirect happens
 *
 * This prevents the flash of marketing content for authenticated users
 * while maintaining full SEO visibility.
 */
export function AuthAwareContent({ children }: AuthAwareContentProps) {
  // Start with null to avoid hydration mismatch
  // Server always renders content, client decides visibility
  const [shouldShow, setShouldShow] = useState<boolean | null>(null);

  useLayoutEffect(() => {
    // Check auth hint synchronously before paint
    const authHint = getAuthHint();
    const lastOrg = getLastOrganization();

    // If user appears logged in (has hint AND last org), keep hidden
    // The AuthRedirect component will handle the actual redirect
    if (authHint && lastOrg) {
      setShouldShow(false);
    } else {
      // No auth hint = definitely logged out, show content
      setShouldShow(true);
    }
  }, []);

  // During SSR and initial hydration, render normally (for SEO)
  // The opacity-0 class is applied only when we've determined user is logged in
  return (
    <div
      className={`min-h-screen bg-white ${
        shouldShow === false ? 'opacity-0' : 'opacity-100'
      } transition-opacity duration-75`}
    >
      {children}
    </div>
  );
}
